import { type ApiPaths, type ErrorCode, isApiErrorBody } from '@dc1/shared';
import createClient, { type Middleware } from 'openapi-fetch';

/** Typed error from the API's { code, message, details?, requestId } body; NETWORK when the server was unreachable. */
export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode | 'NETWORK',
    readonly status: number,
    readonly requestId?: string,
    readonly details?: unknown,
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

/** The API refuses state-changing requests without this header (CSRF defence). */
const csrfHeader: Middleware = {
  onRequest({ request }) {
    if (!SAFE.has(request.method)) request.headers.set('X-Requested-With', 'fetch');
    return request;
  },
};

/** Paths come from the OpenAPI spec: a renamed route or changed shape fails `tsc` here. */
export const api = createClient<ApiPaths>({
  baseUrl: typeof window === 'undefined' ? 'http://localhost' : window.location.origin,
  credentials: 'same-origin',
});
api.use(csrfHeader);

/** Turns an openapi-fetch result into data or a thrown ApiError. */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  let result: { data?: T; error?: unknown; response: Response };
  try {
    result = await call;
  } catch {
    throw new ApiError('NETWORK', 0);
  }
  if (!result.response.ok) {
    const body = result.error;
    if (isApiErrorBody(body))
      throw new ApiError(body.code, result.response.status, body.requestId, body.details);
    throw new ApiError('INTERNAL', result.response.status);
  }
  return result.data as T;
}
